import { c as _c } from "react/compiler-runtime";
import { Idv2, setExternalBack, useIdv2 } from '@onefootprint/idv2';
import type { OnboardingRequirement } from '@onefootprint/request-types';
import { UserChallengeActionKind } from '@onefootprint/types';
import { useCallback, useEffect } from 'react';
import { type DeviceInfo, useLogStateMachine } from '@/idv/hooks';
import { CollectDocument, Liveness } from '@/idv/plugins';
import { useOnboardingMachine } from '../../components/machine-provider';
import { getRequirements } from '../../utils/get-requirement';
import { getPreferUploadDoc } from '../../utils/state-machine/machine.utils';
const shouldRunIdDoc = (deviceInfo: DeviceInfo, requirements: OnboardingRequirement[], isTransfer?: boolean): boolean => {
  const isDeviceMobile = deviceInfo.type === 'mobile';
  const isDeviceTablet = deviceInfo.type === 'tablet';
  const isTabletOrMobile = isDeviceMobile || isDeviceTablet;
  if (isTransfer && !isTabletOrMobile) {
    // If we're running the transfer app on desktop, we want to keep the transfer as small as
    // possible. So, only register the passkey, don't allow also uploading id doc in desktop transfer
    return false;
  }
  return requirements[0]?.kind === 'collect_document';
};
const shouldRunLiveness = (requirements: OnboardingRequirement[]): boolean => {
  return requirements[0]?.kind === 'liveness';
};
const Router = () => {
  const $ = _c(40);
  const [state, send] = useOnboardingMachine();
  const {
    idvContext: baseIdvContext,
    onboardingContext: t0
  } = state.context;
  const {
    config,
    idDocOutcome
  } = t0;
  const {
    data: t1,
    onHandoffComplete,
    navigation
  } = useIdv2();
  const {
    allRequirements,
    canGoBack,
    relayToIDV2
  } = t1;
  let t2;
  if ($[0] !== navigation || $[1] !== send) {
    t2 = async () => {
      await navigation.back();
      send({
        type: "goBackRequirementsReceived"
      });
    };
    $[0] = navigation;
    $[1] = send;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const legacyBack = t2;
  let t3;
  let t4;
  if ($[3] !== legacyBack || $[4] !== relayToIDV2) {
    t3 = () => {
      if (relayToIDV2) {
        return;
      }
      setExternalBack(legacyBack);
      return _temp;
    };
    t4 = [relayToIDV2, legacyBack];
    $[3] = legacyBack;
    $[4] = relayToIDV2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const idDocReqs = getRequirements(allRequirements, "collect_document");
  useLogStateMachine("onboarding-requirements", state);
  let t5;
  if ($[7] !== navigation || $[8] !== send) {
    t5 = async () => {
      await navigation.forward();
      send({
        type: "requirementCompleted"
      });
    };
    $[7] = navigation;
    $[8] = send;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleRequirementCompleted = t5;
  let t6;
  if ($[10] !== state) {
    t6 = state.matches("success");
    $[10] = state;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const isDone = t6;
  let t7;
  if ($[12] !== baseIdvContext || $[13] !== isDone || $[14] !== navigation || $[15] !== onHandoffComplete) {
    t7 = () => {
      if (!isDone) {
        return;
      }
      if (baseIdvContext.isTransfer) {
        onHandoffComplete();
      } else {
        navigation.forward();
      }
    };
    $[12] = baseIdvContext;
    $[13] = isDone;
    $[14] = navigation;
    $[15] = onHandoffComplete;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== isDone) {
    t8 = [isDone];
    $[17] = isDone;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t9;
  if ($[19] !== navigation || $[20] !== send) {
    t9 = async () => {
      await navigation.back();
      send({
        type: "goBackRequirementsReceived"
      });
    };
    $[19] = navigation;
    $[20] = send;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== canGoBack || $[23] !== t9) {
    t10 = {
      canGoBack,
      onBack: t9
    };
    $[22] = canGoBack;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== baseIdvContext || $[26] !== t10) {
    t11 = {
      ...baseIdvContext,
      backContext: t10
    };
    $[25] = baseIdvContext;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const idvContext = t11;
  if (relayToIDV2) {
    let t12;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Idv2 />;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    return t12;
  }
  if (shouldRunIdDoc(baseIdvContext.device, allRequirements, baseIdvContext.isTransfer)) {
    const t12 = getPreferUploadDoc(idDocReqs) || idDocReqs[0];
    let t13;
    if ($[29] !== config || $[30] !== idDocOutcome || $[31] !== t12) {
      t13 = {
        requirement: t12,
        sandboxOutcome: idDocOutcome,
        config
      };
      $[29] = config;
      $[30] = idDocOutcome;
      $[31] = t12;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] !== handleRequirementCompleted || $[34] !== idvContext || $[35] !== t13) {
      t14 = <CollectDocument idvContext={idvContext} context={t13} onDone={handleRequirementCompleted} />;
      $[33] = handleRequirementCompleted;
      $[34] = idvContext;
      $[35] = t13;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    return t14;
  }
  if (shouldRunLiveness(allRequirements)) {
    let t12;
    if ($[37] !== handleRequirementCompleted || $[38] !== idvContext) {
      t12 = <Liveness actionKind={UserChallengeActionKind.addPrimary} idvContext={idvContext} onDone={handleRequirementCompleted} />;
      $[37] = handleRequirementCompleted;
      $[38] = idvContext;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    return t12;
  }
  return null;
};
export default Router;
function _temp() {
  return setExternalBack(undefined);
}